/**
 * resolveRuntimeConfig — плоский RuntimeWidgetConfig из split-источника (или
 * legacy design_config). ZOD-FREE (вызывается в рантайме виджета). Типы — только
 * `import type` (стираются), поэтому пакет не тянет рантайм-зависимости из shared.
 */
import type { BehaviorConfig, RuntimeWidgetConfig, WidgetConfigSource, WidgetType } from '@inbx/shared/schemas';

const DEFAULT_TEMPLATE: Record<WidgetType, string> = {
  chat: 'chat-support',
  form: 'form-card',
  survey: 'survey-card',
  promo: 'promo-modal',
};

function isEmpty(o: Record<string, unknown> | null | undefined): boolean {
  return !o || Object.keys(o).length === 0;
}

function legacyContent(type: WidgetType, dc: Record<string, unknown>): Record<string, unknown> {
  if (type === 'chat')
    return { title: dc['title'], greeting: dc['welcome_message'], placeholder: dc['placeholder'], offline_message: dc['offline_message'] };
  if (type === 'form')
    return { fields: dc['fields'] ?? [], submit_label: dc['submit_label'], success_message: dc['success_message'], reply_via: dc['reply_via'] };
  if (type === 'survey')
    return { questions: dc['questions'] ?? [], completion_message: dc['completion_message'], show_progress: dc['show_progress'], allow_back: dc['allow_back'], reply_via: dc['reply_via'] };
  return { headline: dc['headline'], body_text: dc['body_text'], cta_label: dc['cta_label'], cta_url: dc['cta_url'] };
}

export function resolveRuntimeConfig(src: WidgetConfigSource): RuntimeWidgetConfig {
  const type = src.type;
  const themeRaw = (src.themeConfig ?? {}) as { template_id?: string; vars?: Record<string, string> };
  let content = (src.contentConfig ?? {}) as Record<string, unknown>;
  const behaviorRaw = (src.behaviorConfig ?? {}) as Record<string, unknown>;
  const mediaRaw = (src.mediaConfig ?? {}) as Record<string, unknown>;

  if (isEmpty(content) && !isEmpty(src.designConfig)) {
    content = legacyContent(type, src.designConfig as Record<string, unknown>);
  }

  const behavior: BehaviorConfig = {
    ...behaviorRaw,
    mode: (behaviorRaw['mode'] as BehaviorConfig['mode']) ?? 'popup',
    position: (behaviorRaw['position'] as BehaviorConfig['position']) ?? 'bottom-right',
    auto_open_after: (behaviorRaw['auto_open_after'] as number) ?? 0,
    show_once_per: (behaviorRaw['show_once_per'] as BehaviorConfig['show_once_per']) ?? 'session',
    dismissible: (behaviorRaw['dismissible'] as boolean) ?? true,
    scenario_id: (behaviorRaw['scenario_id'] as string | null | undefined) ?? null,
  };

  return {
    widgetId: src.widgetId,
    publicKey: src.publicKey,
    workspaceId: src.workspaceId,
    type,
    templateId: themeRaw.template_id || DEFAULT_TEMPLATE[type],
    vars: themeRaw.vars ?? {},
    content: content as RuntimeWidgetConfig['content'],
    behavior,
    media: { ...mediaRaw } as RuntimeWidgetConfig['media'],
    customCssUrl: src.customCssUrl ?? null,
    botScenarioId: src.botScenarioId ?? behavior.scenario_id ?? null,
  };
}
